import type { Expense, PayoutReceipt } from '@prisma/client';
import Decimal from 'decimal.js';
import { z } from 'zod';
import { HttpError } from '../../core/errors.js';
import { nextDocumentNumber } from '../../core/sequence.js';
import { postEntry, reverseEntry, type PostingLine } from '../accounting/posting.service.js';
import type { AuthContext } from '../auth/auth.types.js';
import { BASE_CURRENCY, computeTotals } from '../sales/calc.js';
import { logEvent, toDate } from '../sales/documents.service.js';
import {
  assertApprovalLimit,
  baseRound,
  dec,
  ensureExpenseCategories,
  outstandingOf,
  refreshBillStatus,
  type Db,
  type Tx,
} from './purchases.service.js';

const money = z
  .union([z.string(), z.number()])
  .transform((v) => String(v).trim())
  .refine((v) => /^\d{1,15}(\.\d{1,4})?$/.test(v), 'Enter a valid amount');
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');
const currencyField = z
  .string()
  .trim()
  .length(3)
  .transform((v) => v.toUpperCase())
  .optional();

async function rateFor(
  db: Db,
  companyId: number,
  currency: string,
  date: Date,
  given?: string,
): Promise<Decimal> {
  if (currency === BASE_CURRENCY) return new Decimal(1);
  if (given) return dec(given);
  const latest = await db.exchangeRate.findFirst({
    where: { companyId, currency, effectiveDate: { lte: date } },
    orderBy: { effectiveDate: 'desc' },
  });
  if (!latest)
    throw new HttpError(422, 'NO_EXCHANGE_RATE', `Enter the exchange rate for ${currency}`);
  return dec(latest.rate);
}

// ───────────────────────── expenses ─────────────────────────

export const expenseInputSchema = z
  .object({
    categoryId: z.number().int().positive(),
    partyId: z.number().int().positive().nullable().optional(),
    expenseDate: isoDate,
    description: z.string().trim().min(2, 'Describe the expense').max(300),
    currency: currencyField,
    exchangeRate: money.optional(),
    /** Amount before VAT. */
    amount: money.refine((v) => Number(v) > 0, 'Amount must be greater than zero'),
    vatCode: z.string().trim().min(2).max(30).default('VAT_STD'),
    paidBy: z.enum(['company', 'staff']).default('company'),
    financialAccountId: z.number().int().positive().nullable().optional(),
    reference: z.string().trim().max(100).nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.paidBy === 'company' && !v.financialAccountId)
      ctx.addIssue({
        code: 'custom',
        path: ['financialAccountId'],
        message: 'Choose the account it was paid from',
      });
    if (v.paidBy === 'staff' && !v.partyId)
      ctx.addIssue({
        code: 'custom',
        path: ['partyId'],
        message: 'Choose the staff member to reimburse',
      });
  });
export type ExpenseInput = z.infer<typeof expenseInputSchema>;

/** Records and posts an expense. Posting is the approval, so the creator's limit applies. */
export async function createExpense(
  tx: Tx,
  auth: AuthContext,
  input: ExpenseInput,
  opts: { number?: string } = {},
): Promise<Expense> {
  await ensureExpenseCategories(tx, auth.companyId);
  const category = await tx.expenseCategory.findFirst({
    where: { id: input.categoryId, companyId: auth.companyId, active: true },
  });
  if (!category) throw new HttpError(400, 'VALIDATION_ERROR', 'Expense category not found');
  const vat = await tx.taxRate.findFirst({
    where: { companyId: auth.companyId, kind: 'vat', code: input.vatCode, active: true },
  });
  if (!vat)
    throw new HttpError(400, 'VALIDATION_ERROR', `VAT category "${input.vatCode}" does not exist`);
  let party = null;
  if (input.partyId) {
    party = await tx.party.findFirst({
      where: { id: input.partyId, companyId: auth.companyId, active: true },
    });
    if (!party || !party.isVendor)
      throw new HttpError(
        400,
        'VALIDATION_ERROR',
        'Choose a vendor or staff member (a party marked as vendor)',
      );
  }
  let account = null;
  if (input.paidBy === 'company') {
    account = await tx.financialAccount.findFirst({
      where: { id: input.financialAccountId!, companyId: auth.companyId },
    });
    if (!account || account.status !== 'active')
      throw new HttpError(400, 'VALIDATION_ERROR', 'Choose an active account to pay from');
  }
  const date = toDate(input.expenseDate);
  const currency = input.currency ?? account?.currency ?? party?.currency ?? BASE_CURRENCY;
  if (account && account.currency !== currency)
    throw new HttpError(
      409,
      'CURRENCY_MISMATCH',
      `That account holds ${account.currency}; record the expense in ${account.currency}`,
    );
  const rate = await rateFor(tx, auth.companyId, currency, date, input.exchangeRate);
  const calc = computeTotals(
    [
      {
        kind: 'item',
        quantity: 1,
        unitPrice: input.amount,
        discountType: 'none',
        discountValue: 0,
        vatRate: vat.rate.toString(),
      },
    ],
    { currency, exchangeRate: rate },
  );
  assertApprovalLimit(auth, calc.baseTotal);
  const number = opts.number ?? (await nextDocumentNumber(tx, auth.companyId, 'expense', date));
  const expense = await tx.expense.create({
    data: {
      companyId: auth.companyId,
      number,
      categoryId: category.id,
      partyId: party?.id ?? null,
      expenseDate: date,
      description: input.description,
      currency,
      exchangeRate: rate.toString(),
      amount: input.amount,
      vatCode: input.vatCode,
      vatAmount: calc.taxTotal.toString(),
      total: calc.total.toString(),
      baseTotal: calc.baseTotal.toString(),
      paidBy: input.paidBy,
      financialAccountId: account?.id ?? null,
      reference: input.reference ?? null,
      status: 'posted',
      createdBy: auth.userId,
    },
  });
  // Dr expense (net) + VAT input / Cr the account it was paid from (or Accounts Payable for staff).
  const netBase = baseRound(dec(calc.lines[0]!.lineNet).times(rate));
  const vatBase = baseRound(dec(calc.lines[0]!.lineTax).times(rate));
  const lines: PostingLine[] = [
    { accountId: category.accountId, debit: netBase.toFixed(4), memo: input.description },
  ];
  if (!vatBase.isZero())
    lines.push({ systemKey: 'VAT_IN', debit: vatBase.toFixed(4), memo: number });
  const totalBase = netBase.plus(vatBase);
  lines.push(
    account
      ? {
          accountId: account.ledgerAccountId,
          credit: totalBase.toFixed(4),
          memo: input.reference ?? number,
        }
      : { systemKey: 'AP', credit: totalBase.toFixed(4), memo: `Staff reimbursement ${number}` },
  );
  const entry = await postEntry(tx, {
    companyId: auth.companyId,
    date: input.expenseDate,
    description: `Expense ${number}: ${input.description}`,
    sourceType: 'expense',
    sourceId: expense.id,
    reference: input.reference ?? number,
    lines,
    userId: auth.userId,
  });
  await logEvent(tx, {
    companyId: auth.companyId,
    entityType: 'expense',
    entityId: expense.id,
    type: 'created',
    userId: auth.userId,
    note: number,
  });
  return tx.expense.update({ where: { id: expense.id }, data: { journalEntryId: entry.id } });
}

export async function voidExpense(tx: Tx, auth: AuthContext, id: number, reason: string) {
  const e = await tx.expense.findFirst({ where: { id, companyId: auth.companyId } });
  if (!e) throw new HttpError(404, 'NOT_FOUND', 'Expense not found');
  if (e.status === 'void') throw new HttpError(409, 'ALREADY_VOID', 'This expense is already void');
  const paid = await tx.payoutAllocation.count({
    where: { targetType: 'expense', targetId: id, payout: { status: 'posted' } },
  });
  if (paid > 0 || dec(e.amountPaid).gt(0))
    throw new HttpError(
      409,
      'HAS_PAYMENTS',
      'This expense has been reimbursed. Void that payout first.',
    );
  const rev = e.journalEntryId
    ? await reverseEntry(tx, {
        companyId: auth.companyId,
        entryId: e.journalEntryId,
        reason: `Void ${e.number}: ${reason}`,
        date: new Date().toISOString().slice(0, 10),
        userId: auth.userId,
      })
    : null;
  const updated = await tx.expense.update({
    where: { id },
    data: {
      status: 'void',
      voidedAt: new Date(),
      voidReason: reason,
      voidEntryId: rev?.id ?? null,
    },
  });
  await logEvent(tx, {
    companyId: auth.companyId,
    entityType: 'expense',
    entityId: id,
    type: 'void',
    userId: auth.userId,
    note: reason,
  });
  return updated;
}

// ───────────────────────── payout receipts ─────────────────────────

export const payoutInputSchema = z.object({
  partyId: z.number().int().positive(),
  payoutDate: isoDate,
  financialAccountId: z.number().int().positive(),
  currency: currencyField,
  exchangeRate: money.optional(),
  /** Cash actually paid, in `currency`. */
  amount: money.refine((v) => Number(v) > 0, 'Amount must be greater than zero'),
  /** Tax we withheld from the vendor and owe to URA. */
  whtWithheld: money.default('0'),
  reference: z.string().trim().max(100).nullable().optional(),
  notes: z.string().trim().max(500).nullable().optional(),
  allocations: z
    .array(
      z.object({
        targetType: z.enum(['bill', 'expense']),
        targetId: z.number().int().positive(),
        amount: money.refine((v) => Number(v) > 0),
      }),
    )
    .max(100)
    .default([]),
  /** With no allocations, settle the oldest open bills and reimbursements first. */
  autoAllocate: z.boolean().default(false),
});
export type PayoutInput = z.infer<typeof payoutInputSchema>;

interface Payable {
  type: 'bill' | 'expense';
  id: number;
  label: string;
  currency: string;
  rate: Decimal;
  owed: Decimal;
  date: Date;
}

/** Everything a vendor is owed for, oldest first: approved bills and unpaid staff reimbursements. */
export async function openPayables(
  db: Db,
  companyId: number,
  partyId: number,
  currency?: string,
): Promise<Payable[]> {
  const bills = await db.vendorBill.findMany({
    where: {
      companyId,
      partyId,
      status: { in: ['approved', 'partially_paid'] },
      ...(currency ? { currency } : {}),
    },
  });
  const expenses = await db.expense.findMany({
    where: {
      companyId,
      partyId,
      paidBy: 'staff',
      status: { in: ['posted', 'partially_paid'] },
      ...(currency ? { currency } : {}),
    },
  });
  return [
    ...bills.map<Payable>((b) => ({
      type: 'bill',
      id: b.id,
      label: b.number ?? `Bill ${b.id}`,
      currency: b.currency,
      rate: dec(b.exchangeRate),
      owed: outstandingOf(b),
      date: b.dueDate ?? b.issueDate,
    })),
    ...expenses.map<Payable>((e) => ({
      type: 'expense',
      id: e.id,
      label: e.number,
      currency: e.currency,
      rate: dec(e.exchangeRate),
      owed: outstandingOf(e as never),
      date: e.expenseDate,
    })),
  ].sort((a, b) => a.date.getTime() - b.date.getTime() || a.id - b.id);
}

export async function createPayout(
  tx: Tx,
  auth: AuthContext,
  input: PayoutInput,
  opts: { payoutNo?: string } = {},
): Promise<PayoutReceipt> {
  const party = await tx.party.findFirst({
    where: { id: input.partyId, companyId: auth.companyId, active: true },
  });
  if (!party || !party.isVendor) throw new HttpError(400, 'VALIDATION_ERROR', 'Vendor not found');
  const account = await tx.financialAccount.findFirst({
    where: { id: input.financialAccountId, companyId: auth.companyId },
  });
  if (!account || account.status !== 'active')
    throw new HttpError(400, 'VALIDATION_ERROR', 'Choose an active bank or payment account');
  const currency = input.currency ?? account.currency;
  if (account.currency !== currency)
    throw new HttpError(409, 'CURRENCY_MISMATCH', `That account holds ${account.currency}`);
  const date = toDate(input.payoutDate);
  const rate = await rateFor(tx, auth.companyId, currency, date, input.exchangeRate);
  const amount = dec(input.amount);
  const wht = dec(input.whtWithheld);
  const settlement = amount.plus(wht); // what the payment settles on the vendor's side
  const payables = await openPayables(tx, auth.companyId, party.id, currency);

  let allocations = input.allocations.map((a) => ({ ...a, amount: dec(a.amount) }));
  if (allocations.length === 0 && input.autoAllocate) {
    let left = settlement;
    allocations = [];
    for (const p of payables) {
      if (left.lte(0)) break;
      const pay = Decimal.min(left, p.owed);
      if (pay.gt(0)) allocations.push({ targetType: p.type, targetId: p.id, amount: pay });
      left = left.minus(pay);
    }
  }
  if (allocations.length === 0)
    throw new HttpError(422, 'NOTHING_TO_PAY', 'Choose the bills or expenses this payment settles');
  const seen = new Set<string>();
  for (const a of allocations) {
    const k = `${a.targetType}:${a.targetId}`;
    if (seen.has(k))
      throw new HttpError(
        400,
        'VALIDATION_ERROR',
        'A bill can appear only once in the allocations',
      );
    seen.add(k);
  }
  const allocated = allocations.reduce((s, a) => s.plus(a.amount), new Decimal(0));
  if (!allocated.equals(settlement))
    throw new HttpError(
      409,
      allocated.gt(settlement) ? 'OVER_ALLOCATED' : 'UNALLOCATED',
      allocated.gt(settlement)
        ? 'The allocations are more than the amount paid (plus tax withheld)'
        : 'The payment must be fully allocated to bills or expenses (advances to vendors are not supported)',
    );
  if (wht.gt(0) && wht.gte(settlement))
    throw new HttpError(
      400,
      'VALIDATION_ERROR',
      'Tax withheld must be less than the amount settled',
    );

  for (const a of allocations) {
    const p = payables.find((x) => x.type === a.targetType && x.id === a.targetId);
    if (!p)
      throw new HttpError(
        409,
        'NOT_PAYABLE',
        `${a.targetType === 'bill' ? 'Bill' : 'Expense'} ${a.targetId} is not open for this vendor in ${currency}`,
      );
    if (a.amount.gt(p.owed))
      throw new HttpError(409, 'OVERPAYMENT', `${p.label}: only ${p.owed.toString()} is owed`);
  }

  const payoutNo = opts.payoutNo ?? (await nextDocumentNumber(tx, auth.companyId, 'payout', date));
  const payout = await tx.payoutReceipt.create({
    data: {
      companyId: auth.companyId,
      payoutNo,
      partyId: party.id,
      payoutDate: date,
      financialAccountId: account.id,
      currency,
      exchangeRate: rate.toString(),
      amount: amount.toString(),
      whtWithheld: wht.toString(),
      reference: input.reference ?? null,
      notes: input.notes ?? null,
      allocatedAmount: allocated.toString(),
      status: 'posted',
      createdBy: auth.userId,
      allocations: {
        create: allocations.map((a) => ({
          targetType: a.targetType,
          targetId: a.targetId,
          amount: a.amount.toString(),
        })),
      },
    },
  });

  // Dr Accounts Payable (per bill, at the bill's rate) / Cr bank / Cr WHT Payable; FX difference last.
  const cashBase = baseRound(amount.times(rate));
  const whtBase = baseRound(wht.times(rate));
  const lines: PostingLine[] = [];
  let debits = new Decimal(0);
  for (const a of allocations) {
    const p = payables.find((x) => x.type === a.targetType && x.id === a.targetId)!;
    const apBase = baseRound(a.amount.times(p.rate));
    debits = debits.plus(apBase);
    lines.push({ systemKey: 'AP', debit: apBase.toFixed(4), memo: p.label });
  }
  lines.push({
    accountId: account.ledgerAccountId,
    credit: cashBase.toFixed(4),
    memo: input.reference ?? payoutNo,
  });
  if (whtBase.gt(0))
    lines.push({ systemKey: 'WHT_PAY', credit: whtBase.toFixed(4), memo: 'Withheld from vendor' });
  const diff = debits.minus(cashBase.plus(whtBase));
  if (diff.gt(0))
    lines.push({ systemKey: 'FX_GAIN', credit: diff.toFixed(4), memo: 'Realised exchange gain' });
  if (diff.lt(0))
    lines.push({
      systemKey: 'FX_LOSS',
      debit: diff.abs().toFixed(4),
      memo: 'Realised exchange loss',
    });
  const entry = await postEntry(tx, {
    companyId: auth.companyId,
    date: input.payoutDate,
    description: `Payout ${payoutNo}: ${party.name}`,
    sourceType: 'payout',
    sourceId: payout.id,
    reference: input.reference ?? payoutNo,
    lines,
    userId: auth.userId,
  });

  for (const a of allocations) {
    if (a.targetType === 'bill') {
      const b = await tx.vendorBill.findUniqueOrThrow({ where: { id: a.targetId } });
      await tx.vendorBill.update({
        where: { id: b.id },
        data: { amountPaid: dec(b.amountPaid).plus(a.amount).toString() },
      });
      await refreshBillStatus(tx, b.id);
      await logEvent(tx, {
        companyId: auth.companyId,
        entityType: 'vendor_bill',
        entityId: b.id,
        type: 'payment',
        userId: auth.userId,
        note: `${a.amount.toString()} ${currency} (${payoutNo})`,
      });
    } else {
      const e = await tx.expense.findUniqueOrThrow({ where: { id: a.targetId } });
      const paid = dec(e.amountPaid).plus(a.amount);
      await tx.expense.update({
        where: { id: e.id },
        data: {
          amountPaid: paid.toString(),
          status: paid.gte(dec(e.total)) ? 'paid' : 'partially_paid',
        },
      });
    }
  }
  await logEvent(tx, {
    companyId: auth.companyId,
    entityType: 'payout',
    entityId: payout.id,
    type: 'created',
    userId: auth.userId,
    note: payoutNo,
  });
  return tx.payoutReceipt.update({ where: { id: payout.id }, data: { journalEntryId: entry.id } });
}

export async function voidPayout(tx: Tx, auth: AuthContext, id: number, reason: string) {
  const p = await tx.payoutReceipt.findFirst({
    where: { id, companyId: auth.companyId },
    include: { allocations: true },
  });
  if (!p) throw new HttpError(404, 'NOT_FOUND', 'Payout not found');
  if (p.status === 'void') throw new HttpError(409, 'ALREADY_VOID', 'This payout is already void');
  const rev = p.journalEntryId
    ? await reverseEntry(tx, {
        companyId: auth.companyId,
        entryId: p.journalEntryId,
        reason: `Void ${p.payoutNo}: ${reason}`,
        date: new Date().toISOString().slice(0, 10),
        userId: auth.userId,
      })
    : null;
  for (const a of p.allocations) {
    if (a.targetType === 'bill') {
      const b = await tx.vendorBill.findUnique({ where: { id: a.targetId } });
      if (!b) continue;
      await tx.vendorBill.update({
        where: { id: b.id },
        data: { amountPaid: Decimal.max(0, dec(b.amountPaid).minus(a.amount)).toString() },
      });
      await refreshBillStatus(tx, b.id);
    } else {
      const e = await tx.expense.findUnique({ where: { id: a.targetId } });
      if (!e) continue;
      const paid = Decimal.max(0, dec(e.amountPaid).minus(a.amount));
      await tx.expense.update({
        where: { id: e.id },
        data: { amountPaid: paid.toString(), status: paid.lte(0) ? 'posted' : 'partially_paid' },
      });
    }
  }
  const updated = await tx.payoutReceipt.update({
    where: { id },
    data: {
      status: 'void',
      voidedAt: new Date(),
      voidReason: reason,
      voidEntryId: rev?.id ?? null,
    },
  });
  await logEvent(tx, {
    companyId: auth.companyId,
    entityType: 'payout',
    entityId: id,
    type: 'void',
    userId: auth.userId,
    note: reason,
  });
  return updated;
}
